'use client';

import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { Link } from 'react-router-dom';

const ServiceCard = ({ service, index }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div
      className={`bg-card rounded-2xl shadow-subtle hover:shadow-dramatic transition-all duration-500  border border-border ${
        isExpanded ? 'col-span-full' : ''
      }`}
    >
      {/* Card Header */}
      <div className="relative h-48 sm:h-64 rounded-t-2xl overflow-hidden">
        <Image
          src={service.image}
          alt={service.alt}
          className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-700"
        />
        <div className={`absolute inset-0 bg-gradient-to-t ${service.color} opacity-80`} />

        <div className="absolute inset-0 p-4 sm:p-6 flex flex-col justify-between">
          <div className="flex items-start justify-between">
            <div className="w-8 h-8  shadow-subtle backdrop-blur-sm rounded-2xl flex items-center justify-center">
              <Icon name={service.iconName} size={32} className="text-white" />
            </div>
            <div className="px-4 py-1 sm:py-2 bg-white/20 backdrop-blur-sm rounded-full">
              <span className="text-xs sm:text-sm font-headline font-bold text-white">
                From {service.priceFrom}
              </span>
            </div>
          </div>

          <div>
            <h2 className="text-2xl sm:text-3xl font-headline font-black text-white mb-2">
              {service.title}
            </h2>
            <p className="text-sm sm:text-lg text-white/90 font-body">
              {service.tagline}
            </p>
          </div>
        </div>
      </div>

      {/* Card Content */}
      <div className="p-4 sm:p-6 flex flex-col h-full">
        <div className="flex-1">
        <p className="text-base text-muted-foreground leading-relaxed mb-6 min-h-[4.5rem]">
          {isExpanded ? service.description : service.shortdescription}
        </p>

        {/* Quick Features */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {service.features
            .slice(0, isExpanded ? service.features.length : 2)
            .map((feature, idx) => (
              <div key={idx} className="flex items-start space-x-3">
                <Icon
                  name="CheckCircleIcon"
                  size={20}
                  className="text-success flex-shrink-0 mt-1"
                />
                <div>
                  <h4 className="text-sm font-headline font-bold text-foreground mb-1">
                    {feature.title}
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="space-y-1 sm:space-y-4 pt-2 sm:pt-4 border-t border-border animate-fadeIn">
            {/* Deliverables */}
            <div>
              <h4 className="text-lg font-headline font-bold text-foreground mb-2 sm:mb-2 flex items-center">
                <Icon
                  name="FileText"
                  size={24}
                  className="text-primary mr-2"
                />
                What You Get
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {service.deliverables.map((deliverable, idx) => (
                  <div
                    key={idx}
                    className="flex items-center space-x-2 text-sm text-foreground"
                  >
                    <Icon
                      name="ArrowRightIcon"
                      size={16}
                      className="text-secondary flex-shrink-0"
                    />
                    <span>{deliverable}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Timeline & Investment */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div className="p-2 sm:p-6 bg-muted rounded-xl">
                <div className="flex items-center space-x-3 mb-2 sm:mb-4">
                  <Icon name="ClockIcon" size={24} className="text-primary" />
                  <h4 className="text-base font-headline font-bold text-foreground">
                    Timeline
                  </h4>
                </div>
                <p className="text-xl sm:text-2xl font-headline text-red-600 font-black text-foreground">
                  {service.timeline}
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  From strategy to launch
                </p>
              </div>

              <div className={`p-3 sm:p-6 bg-gradient-to-br ${service.color} rounded-xl`}>
                <div className="flex items-center space-x-3 mb-3">
                  <Icon
                    name="DollarSign"
                    size={24}
                    className="text-white"
                  />
                  <h4 className="text-base font-headline font-bold text-white">
                    Investment
                  </h4>
                </div>
                <p className="text-xl sm:text-2xl font-headline font-black text-white">
                  From {service.priceFrom}
                </p>
                <p className="text-sm text-white/80 mt-2">
                  Custom packages available
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 mt-6">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex-1 px-6 py-3 bg-red-600 hover:bg-red-700 text-white text-foreground font-headline font-bold text-sm rounded-lg transition-all duration-300 flex items-center justify-center space-x-2"
          >
            <span>{isExpanded ? 'Show Less' : 'View Details'}</span>
            <Icon
              name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'}
              size={20}
            />
          </button>

           <Link
  to="/booking-concierge-premium-consultation"
  state={{ preselectedProject: service }}
  className="flex-1 px-6 py-3 bg-primary hover:bg-white hover:text-primary hover:border hover:border-primary hover:shadow-subtle text-white font-headline font-bold text-sm rounded-lg shadow-cta hover:shadow-dramatic transform hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center space-x-2"
>
  <span>Book Consultation</span>
  <Icon name="ArrowRightIcon" size={20} />
</Link>

        </div>
      </div>
      </div>
    </div>
  );
};

export default ServiceCard;
